import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'services', 'contact', 'privacy', 'terms'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d55tk82s3mmh6cm90le0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/1b6e3e6d-e481-4c4d-95b3-ea6966cf3d52.png" 
                alt="BELLIZZI SRL Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>BELLIZZI SRL</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'about', label: 'About' },
                { id: 'services', label: 'VPN Services' },
                { id: 'contact', label: 'Contact' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeSection === item.id
                      ? isScrolled ? 'text-teal-600' : 'text-white'
                      : isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8">
                <i className="ri-shield-check-line text-white"></i>
                <span className="text-white text-sm font-medium">Enterprise VPN Solution</span>
              </div>
              
              <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Secure Your<br />
                <span className="font-serif italic font-light">Digital</span><br />
                Presence
              </h1>
              
              <p className="text-xl text-white/80 mb-10 leading-relaxed max-w-lg">
                Professional VPN solutions designed for businesses and individuals who value privacy and unrestricted access.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToSection('contact')}
                  className="bg-white text-gray-900 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-lg"
                >
                  Get Started
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-white px-8 py-4 font-semibold hover:underline transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </button>
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/9fbb909ac56b0f3bb14c3007a7faf342.jpg" 
                  alt="VPN Security Visualization"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-wider text-gray-600 mb-4 font-semibold">BELLIZZI SRL</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Privacy-First<br />
              <span className="font-serif italic">Technology</span><br />
              Solutions
            </h2>
            <p className="text-lg text-gray-600 mt-6">Protecting your digital freedom</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl p-12 relative min-h-[400px] flex flex-col justify-between">
              <div className="absolute top-8 right-8 w-14 h-14 bg-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                <i className="ri-arrow-right-up-line text-2xl text-orange-500"></i>
              </div>
              
              <div>
                <div className="text-7xl font-serif font-bold text-white mb-2">256</div>
                <div className="text-2xl font-semibold text-white mb-2">Bit Encryption</div>
                <div className="text-lg text-white/90 mb-4">Military-grade security</div>
                <div className="text-base italic text-white/80">Your data, completely protected</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-3xl p-12 relative min-h-[400px] flex flex-col justify-between">
              <div className="absolute top-8 right-8 w-14 h-14 bg-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                <i className="ri-arrow-right-up-line text-2xl text-purple-500"></i>
              </div>
              
              <div>
                <div className="text-7xl font-serif font-bold text-white mb-2">50+</div>
                <div className="text-2xl font-semibold text-white mb-2">Global Server Locations</div>
                <div className="text-lg text-white/90 leading-relaxed">
                  Connect from anywhere in the world with optimized speeds and unlimited bandwidth for seamless browsing
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Services Section */}
      <section id="services" className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="text-gray-900">Advanced Features for</span><br />
              <span className="text-teal-500">Complete Protection</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:row-span-2">
              <div className="relative h-full min-h-[600px] rounded-3xl overflow-hidden group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20representation%20of%20digital%20privacy%20and%20anonymity%20with%20dark%20navy%20to%20deep%20purple%20gradient%20background%2C%20featuring%20encrypted%20data%20symbols%20and%20shield%20icons%2C%20modern%20minimalist%20tech%20aesthetic%20showing%20no-log%20policy%20concept%2C%20secure%20network%20visualization%20with%20geometric%20patterns%20and%20flowing%20light%20particles&width=600&height=800&seq=no-log-policy&orientation=portrait" 
                  alt="No-Log Policy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-3xl font-bold mb-2">No-Log Policy</h3>
                  <p className="text-lg text-white/90">Complete anonymity guaranteed</p>
                </div>
              </div>
            </div>
            
            <div className="relative h-[288px] rounded-3xl overflow-hidden bg-gradient-to-br from-green-300 to-green-400 flex items-center justify-center group cursor-pointer shadow-lg border-2 border-white/20">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-lock-line text-6xl text-white"></i>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-black/40 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white">Kill Switch Protection</h3>
              </div>
            </div>
            
            <div className="relative h-[288px] rounded-3xl overflow-hidden bg-gradient-to-br from-rose-300 to-rose-400 flex items-center justify-center group cursor-pointer shadow-lg border-2 border-white/20">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-flashlight-line text-6xl text-white"></i>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-black/40 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white">Lightning Fast Speeds</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Panoramic%20abstract%20digital%20network%20visualization%20with%20flowing%20light%20particles%20and%20data%20streams%20on%20deep%20blue%20gradient%20background%2C%20featuring%20glowing%20connection%20nodes%20and%20encrypted%20pathways%2C%20modern%20tech%20aesthetic%20with%20bokeh%20effects%20and%20dynamic%20energy%20flow%2C%20cinematic%20wide%20angle%20composition%20showing%20cybersecurity%20and%20VPN%20technology%20concept&width=1920&height=600&seq=cta-background&orientation=landscape" 
            alt="Digital Network"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-blue-800/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex flex-col justify-between py-20">
          <div>
            <h2 className="text-7xl font-bold text-white tracking-tight">start protecting</h2>
          </div>
          
          <div className="flex justify-between items-end">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white text-gray-900 px-10 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-2xl flex items-center space-x-4 group"
            >
              <span className="text-lg">Get Started</span>
              <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </button>
            
            <div className="text-right text-white">
              <p className="text-xl font-light leading-relaxed">
                Experience unrestricted internet access<br />
                with enterprise-grade security
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">We're here to help with your VPN needs</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  Email
                </h3>
                <a href="mailto:info@bellizzisrl.com" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                  info@bellizzisrl.com
                </a>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  Phone
                </h3>
                <a href="tel:+12057363754" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                  +1 2057363754
                </a>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  Address
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  VIA DEI BUCANEVE SNC<br />
                  70026 MODUGNO<br />
                  ITALY
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all disabled:opacity-50"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all disabled:opacity-50"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none disabled:opacity-50"
                    placeholder="How can we help you?"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                
                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    Something went wrong. Please try again.
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-teal-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/1b6e3e6d-e481-4c4d-95b3-ea6966cf3d52.png" 
                  alt="BELLIZZI SRL Logo" 
                  className="h-10 w-auto brightness-0 invert"
                />
                <span className="text-xl font-bold">BELLIZZI SRL</span>
              </div>
              <p className="text-emerald-200 leading-relaxed text-sm">
                Professional VPN solutions<br />
                for modern businesses<br />
                and privacy-conscious users
              </p>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-emerald-300 mb-6 font-semibold">CONTACT</h4>
              <div className="space-y-3">
                <a href="mailto:info@bellizzisrl.com" className="block text-emerald-100 hover:text-white transition-colors cursor-pointer text-sm">
                  info@bellizzisrl.com
                </a>
                <a href="tel:+12057363754" className="block text-emerald-100 hover:text-white transition-colors cursor-pointer text-sm">
                  +1 2057363754
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-emerald-300 mb-6 font-semibold">COMPANY</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('about')} className="block text-emerald-100 hover:text-white transition-colors cursor-pointer text-sm">
                  About Us
                </button>
                <button onClick={() => scrollToSection('services')} className="block text-emerald-100 hover:text-white transition-colors cursor-pointer text-sm">
                  VPN Services
                </button>
                <button onClick={() => scrollToSection('privacy')} className="block text-emerald-100 hover:text-white transition-colors cursor-pointer text-sm">
                  Privacy Policy
                </button>
                <button onClick={() => scrollToSection('terms')} className="block text-emerald-100 hover:text-white transition-colors cursor-pointer text-sm">
                  Terms of Service
                </button>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-emerald-300 mb-6 font-semibold">LOCATION</h4>
              <p className="text-emerald-100 leading-relaxed text-sm">
                VIA DEI BUCANEVE SNC<br />
                70026 MODUGNO<br />
                ITALY
              </p>
            </div>
          </div>
          
          <div className="border-t border-emerald-800 pt-12">
            <div className="text-center">
              <div className="text-8xl font-bold text-emerald-700/30 tracking-tight mb-8">
                BELLIZZI
              </div>
              <div className="flex flex-col md:flex-row justify-between items-center text-sm text-emerald-300 space-y-4 md:space-y-0">
                <p>&copy; 2025 BELLIZZI SRL. All rights reserved.</p>
                <a 
                  href="https://readdy.ai/?ref=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Section */}
      <section id="privacy" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h2>
          
          <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h3>
              <p className="leading-relaxed">
                BELLIZZI SRL is committed to protecting your privacy. We collect minimal information necessary to provide our VPN services, including account information and payment details. We do not log your browsing activity, connection timestamps, or IP addresses.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h3>
              <p className="leading-relaxed">
                The information we collect is used solely to maintain your account, process payments, and provide customer support. We never sell, rent, or share your personal information with third parties for marketing purposes.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">3. Data Security</h3>
              <p className="leading-relaxed">
                We employ industry-standard security measures including 256-bit encryption to protect your data. Our servers are located in secure facilities with strict access controls.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">4. Your Rights</h3>
              <p className="leading-relaxed">
                You have the right to access, modify, or delete your personal information at any time. Contact us at info@bellizzisrl.com for any privacy-related requests.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">5. Updates to This Policy</h3>
              <p className="leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any significant changes via email or through our service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms of Service Section */}
      <section id="terms" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h2>
          
          <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h3>
              <p className="leading-relaxed">
                By using BELLIZZI SRL VPN services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Description</h3>
              <p className="leading-relaxed">
                BELLIZZI SRL provides VPN services that encrypt your internet connection and protect your online privacy. We strive to maintain high service availability but cannot guarantee uninterrupted service.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">3. Acceptable Use</h3>
              <p className="leading-relaxed">
                You agree to use our VPN service only for lawful purposes. Activities including but not limited to illegal file sharing, hacking, or any form of cybercrime are strictly prohibited and may result in immediate termination of service.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">4. Payment and Refunds</h3>
              <p className="leading-relaxed">
                All payments are processed securely. Refund policies are available upon request and are handled on a case-by-case basis within 30 days of purchase.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">5. Limitation of Liability</h3>
              <p className="leading-relaxed">
                BELLIZZI SRL shall not be liable for any indirect, incidental, special, or consequential damages arising from the use or inability to use our services.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">6. Termination</h3>
              <p className="leading-relaxed">
                We reserve the right to terminate or suspend your account at any time for violation of these terms or for any other reason at our sole discretion.
              </p>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">7. Contact Information</h3>
              <p className="leading-relaxed">
                For questions about these Terms of Service, please contact us at info@bellizzisrl.com or call +1 2057363754.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
